import React, { useState } from 'react';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import ConfirmPopup from '../ConfirmPopup/ConfirmPopup';
import IconSave from '@mui/icons-material/SaveAltOutlined';
import IconDelete from '@mui/icons-material/Delete';
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { applicationViewLabel } from '../../pages/appointment/components/applicationMap/applicationFigures';
import { formatQuantity, markKindLabel, toQuantity } from '../../pages/appointment/components/applicationMap/applicationMarks';
import styles from './ApplicationMarkPopup.module.css';

const MAX_NOTE_LENGTH = 300;

// Chave estável de um produto na lista (os padrão não têm id)
const productKey = (product, index) => (product.id == null ? `default-${index}` : String(product.id));

/**
 * Edição de uma marcação do mapa de aplicação: produto, quantidade e nota.
 * Trabalha em rascunho; só "Salvar" devolve as alterações (`onSave(patch)`),
 * "Remover" pede confirmação e chama `onRemove()`. Fechar descarta.
 *
 * Se o produto da marcação não está mais na lista da clínica, ele aparece
 * como opção extra para a marcação não perder o que foi registrado.
 */
function ApplicationMarkPopup({ mark, number, products, onSave, onRemove, OnHide }) {
  const currentIndex = products.findIndex((product) => (
    mark.productId != null ? String(product.id) === String(mark.productId) : product.name === mark.product && product.unit === mark.unit
  ));
  const options = currentIndex >= 0
    ? products.map((product, index) => ({ key: productKey(product, index), product }))
    : [{ key: 'current', product: { id: mark.productId, name: mark.product, unit: mark.unit, color: mark.color } },
      ...products.map((product, index) => ({ key: productKey(product, index), product }))];

  const [draft, setDraft] = useState({
    productKey: currentIndex >= 0 ? productKey(products[currentIndex], currentIndex) : 'current',
    quantity: formatQuantity(mark.quantity),
    note: mark.note || '',
  });
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);

  const update = (patch) => setDraft((current) => ({ ...current, ...patch }));

  function saveHandler() {
    const chosen = (options.find((option) => option.key === draft.productKey) || options[0]).product;
    onSave({
      productId: chosen.id == null ? null : Number(chosen.id),
      product: chosen.name,
      unit: chosen.unit || '',
      color: String(chosen.color || mark.color).toUpperCase(),
      quantity: toQuantity(draft.quantity, 0),
      note: draft.note.trim(),
    });
    OnHide();
  }

  function removeHandler() {
    onRemove();
    OnHide();
  }

  const chosenUnit = ((options.find((option) => option.key === draft.productKey) || options[0]).product.unit) || '';

  const content = (
    <div className={styles.container}>
      <span className={styles.context}>
        {markKindLabel(mark.kind)} · {applicationViewLabel(mark.view)}
      </span>

      <div className={styles.row}>
        <FormControl size="small" fullWidth>
          <InputLabel id="application-mark-popup-product">Produto</InputLabel>
          <Select
            labelId="application-mark-popup-product"
            label="Produto"
            value={draft.productKey}
            onChange={(event) => update({ productKey: event.target.value })}
          >
            {options.map(({ key, product }) => (
              <MenuItem key={key} value={key}>
                <span className={styles.dot} style={{ background: product.color }} />
                {product.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          fullWidth
          label={chosenUnit ? `Quantidade (${chosenUnit})` : 'Quantidade'}
          value={draft.quantity}
          inputProps={{ inputMode: 'decimal', maxLength: 8 }}
          onChange={(event) => update({ quantity: event.target.value })}
        />
      </div>

      <TextField
        size="small"
        fullWidth
        multiline
        minRows={3}
        label="Nota"
        value={draft.note}
        inputProps={{ maxLength: MAX_NOTE_LENGTH }}
        onChange={(event) => update({ note: event.target.value })}
      />
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      <CustomButton
        variant="outlined"
        style="error"
        label="Remover"
        icon={<IconDelete />}
        onClick={() => setShowConfirmRemove(true)}
      />
      <div className={styles.footerRight}>
        <CustomButton variant="outlined" style="secondary" label="Cancelar" onClick={() => OnHide()} />
        <CustomButton variant="contained" style="primary" label="Salvar" icon={<IconSave />} onClick={saveHandler} />
      </div>

      {showConfirmRemove && (
        <ConfirmPopup
          Visibility={showConfirmRemove}
          Title="Remover marcação"
          Description={`Remover a marcação ${number} (${mark.product}) deste atendimento?`}
          ConfirmLabel="Remover"
          OnConfirm={removeHandler}
          OnHide={() => setShowConfirmRemove(false)}
        />
      )}
    </div>
  );

  return (
    <PopupBase
      Title={`Marcação ${number}`}
      Content={content}
      Footer={footer}
      OnClose={() => OnHide()}
      maxWidth="sm"
    />
  );
}

export default ApplicationMarkPopup;
